<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import { Button, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/shared/ui"
import { minutesOfDayInTz } from "../model/timeGrid"
import { useRecordsStore } from "../model/store"

const store = useRecordsStore()
const { t } = useI18n()

const open = computed(() => store.activeSheet === "actions")
const booking = computed(() => store.editingBooking)

const isPast = computed(() => {
  if (!booking.value) return false
  return new Date(booking.value.endsAt) < new Date()
})

const rescheduleDisabled = computed(
  () => !booking.value || isPast.value || booking.value.status !== "scheduled",
)

const cancelDisabled = computed(
  () => !booking.value || booking.value.status !== "scheduled",
)

const rescheduleTitle = computed(() =>
  isPast.value ? t("records.actions.pastTooltip") : undefined,
)

const serviceName = computed(() => {
  if (!booking.value) return "—"
  return store.serviceMap.get(booking.value.serviceId) ?? "—"
})

const masterName = computed(() => {
  if (!booking.value) return "—"
  return store.workingMasters.find((m) => m.id === booking.value!.masterId)?.name ?? "—"
})

/** Format a UTC ISO instant as HH:MM in salon tz. */
function formatHHMM(iso: string): string {
  const tz = store.salonTz
  const minOfDay = minutesOfDayInTz(iso, tz)
  const h = Math.floor(minOfDay / 60)
  const m = minOfDay % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
}

const timeRange = computed(() => {
  if (!booking.value) return ""
  return `${formatHHMM(booking.value.startsAt)}–${formatHHMM(booking.value.endsAt)}`
})

function handleReschedule(): void {
  if (!booking.value || rescheduleDisabled.value) return
  store.openReschedule(booking.value)
}

function handleCancel(): void {
  if (!booking.value || cancelDisabled.value) return
  store.openCancel(booking.value)
}

function handleDismiss(): void {
  store.closeSheet()
}
</script>

<template>
  <Sheet :open="open" @update:open="(v) => { if (!v) store.closeSheet() }">
    <SheetContent side="bottom">
      <SheetHeader>
        <SheetTitle>{{ t("journal.a11y.bookingActions") }}</SheetTitle>
      </SheetHeader>

      <div v-if="booking" class="actions-sheet__summary">
        <p class="actions-sheet__service">{{ serviceName }}</p>
        <p class="actions-sheet__master-time">{{ masterName }} · {{ timeRange }}</p>
        <p class="actions-sheet__client">{{ booking.clientName }} · {{ booking.clientPhone }}</p>
      </div>

      <SheetFooter class="actions-sheet__footer">
        <Button
          data-testid="btn-reschedule"
          variant="outline"
          :aria-disabled="String(rescheduleDisabled)"
          :title="rescheduleTitle"
          class="w-full"
          @click="handleReschedule"
        >
          {{ t("journal.actions.reschedule") }}
        </Button>

        <Button
          data-testid="btn-cancel"
          variant="destructive"
          :disabled="cancelDisabled"
          class="w-full"
          @click="handleCancel"
        >
          {{ t("journal.actions.cancel") }}
        </Button>

        <Button
          data-testid="btn-dismiss"
          variant="ghost"
          class="w-full"
          @click="handleDismiss"
        >
          {{ t("journal.cancel.close") }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.actions-sheet__summary {
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.actions-sheet__service {
  font-size: 1rem;
  font-weight: 600;
  color: var(--foreground);
}

.actions-sheet__master-time {
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

.actions-sheet__client {
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

.actions-sheet__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
}
</style>
